/**
 * Resolver Registry
 *
 * Single Map<DatabaseEngineId, OrchestratorResolver> with the canonical
 * resolver assignment per engine. All five user engines are routed to their
 * dialect-native resolvers; `mock` is routed to `mockResolver`.
 *
 * `getResolverRegistry()` returns the singleton. The orchestrator throws on
 * unknown engines so the registry stays exhaustive — when a new
 * `DatabaseEngineId` is added to `engineTypes.ts`, this file MUST gain a
 * corresponding entry.
 *
 * `setResolver(engineId, resolver)` is provided for tests + future slices
 * that want to swap an entry; consumers should never mutate the underlying
 * map directly.
 */

import type { DatabaseEngineId } from '@/core/engines';
import { mssqlResolver } from '@/features/intellisense/resolvers/mssqlResolver';
import { mysqlResolver } from '@/features/intellisense/resolvers/mysqlResolver';
import { oracleResolver } from '@/features/intellisense/resolvers/oracleResolver';
import { pgsqlResolver } from '@/features/intellisense/resolvers/pgsqlResolver';
import { sqliteResolver } from '@/features/intellisense/resolvers/sqliteResolver';

import { mockResolver } from './mockResolver';
import type { OrchestratorResolver } from './types';

export type ResolverRegistry = {
  readonly get: (engineId: DatabaseEngineId) => OrchestratorResolver;
  readonly has: (engineId: DatabaseEngineId) => boolean;
  readonly set: (engineId: DatabaseEngineId, resolver: OrchestratorResolver) => void;
  readonly list: () => readonly DatabaseEngineId[];
};

function buildDefaultRegistry(): Map<DatabaseEngineId, OrchestratorResolver> {
  const map = new Map<DatabaseEngineId, OrchestratorResolver>();
  // MSSQL → dialect-native resolver (PRD Slice "MSSQL Resolver — Core").
  map.set('mssql', mssqlResolver);
  // PG → dialect-native resolver (PRD Slice "PostgreSQL Resolver — Core").
  map.set('pgsql', pgsqlResolver);
  // MySQL → dialect-native resolver (PRD Slice "MySQL Resolver — Core").
  map.set('mysql', mysqlResolver);
  // Oracle → dialect-native resolver (PRD Slice "Oracle Resolver — Core").
  map.set('oracle', oracleResolver);
  // SQLite → dialect-native resolver (PRD Slice "SQLite Resolver — Core").
  map.set('sqlite', sqliteResolver);
  map.set('mock', mockResolver);
  return map;
}

let singleton: Map<DatabaseEngineId, OrchestratorResolver> | null = null;

function ensureSingleton(): Map<DatabaseEngineId, OrchestratorResolver> {
  if (!singleton) {
    singleton = buildDefaultRegistry();
  }
  return singleton;
}

export function getResolverRegistry(): ResolverRegistry {
  const map = ensureSingleton();
  return {
    get: (engineId) => {
      const resolver = map.get(engineId);
      if (!resolver) {
        throw new Error(
          `No completion resolver registered for engine '${engineId}'. ` +
            'Update src/features/intellisense/orchestrator/resolverRegistry.ts.',
        );
      }
      return resolver;
    },
    has: (engineId) => map.has(engineId),
    set: (engineId, resolver) => {
      map.set(engineId, resolver);
    },
    list: () => Array.from(map.keys()),
  };
}

/**
 * Test seam — restores the registry to its default state. Tests that swap
 * resolvers via `setResolver` should call this in `afterEach`.
 */
export function __resetResolverRegistryForTest(): void {
  singleton = buildDefaultRegistry();
}
